// Runtime environment, read once from window.__ENV__ (written into
// /config/env-config.js by the deploy's init container) and deleted
// immediately so no later script can rewrite it between bootstrap and the
// first fetch. In dev the global is absent and every base URL stays "",
// which means relative paths — the Vite proxy handles them.

interface RawEnv {
    apiBaseUrl?: string;
    assetApiBaseUrl?: string;
    ssoStartUrl?: string;
    accountsApiBaseUrl?: string;
    platformApiBaseUrl?: string;
    accountPortalUrl?: string;
}

declare global {
    interface Window {
        __ENV__?: RawEnv;
    }
}

const ALLOWED_HOST =
    /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.(pej\.io|pej\.se)(\/|$)|^https?:\/\/(localhost|127\.0\.0\.1|[a-z0-9-]+(\.[a-z0-9-]+)*\.k3d\.local)(:\d+)?(\/|$)/i;

function validated(name: string, value: string | undefined): string | undefined {
    if (!value) return undefined;
    if (!ALLOWED_HOST.test(value)) {
        // A rejected URL is treated as unconfigured rather than trusted.
        console.error(`env: ${name} rejected by the host allow-list: ${value}`);
        return undefined;
    }
    return value.replace(/\/$/, "");
}

// Whether the deploy injected anything at all, captured before the delete below.
// This — not the value of any single URL — is what tells a prod build apart from
// one whose init container never ran.
const injected = typeof window !== "undefined" && window.__ENV__ != null;
const raw: RawEnv = injected && window.__ENV__ ? window.__ENV__ : {};
if (typeof window !== "undefined") {
    delete window.__ENV__;
}

// Build-time VITE_* values are a dev/k3d fallback only; runtime injection
// always wins, and production never relies on the build-time values.
const apiBaseUrl = validated("apiBaseUrl", raw.apiBaseUrl ?? (import.meta.env.VITE_API_BASE_URL as string | undefined));

export const env = Object.freeze({
    apiBaseUrl: apiBaseUrl ?? "",
    assetApiBaseUrl: validated(
        "assetApiBaseUrl",
        raw.assetApiBaseUrl ?? (import.meta.env.VITE_ASSET_API_BASE_URL as string | undefined),
    ),
    ssoStartUrl: validated("ssoStartUrl", raw.ssoStartUrl),
    // Accounts BFF (sign-out), platform API (product switcher) and the account
    // portal — optional; absent in dev, where those affordances degrade gracefully.
    accountsApiBaseUrl: validated("accountsApiBaseUrl", raw.accountsApiBaseUrl),
    platformApiBaseUrl: validated("platformApiBaseUrl", raw.platformApiBaseUrl),
    accountPortalUrl: validated("accountPortalUrl", raw.accountPortalUrl),
    // Prod build expected runtime injection; without it every request would
    // 404 confusingly — main.tsx renders a static panel instead. Keyed on the
    // injection having happened, NOT on apiBaseUrl being set: ewt-pej leaves
    // apiBaseUrl deliberately unset because frontend-admin and the backend share
    // one host, so "" (relative paths) is the correct production value. Keying on
    // the URL rendered the panel on every production page load instead of the app.
    configMissing: import.meta.env.PROD && !injected,
});
